import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    Typography,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    useTheme,
    Chip
} from '@mui/material';
import { Close, CalendarToday, Search, Euro, Person, Label } from '@mui/icons-material';
import { FormattedMessage } from 'react-intl'; // Assuming react-intl is used based on other files
import { Expense } from '../types/Expense';
import { designTokens } from '../theme/designTokens';
import { getCategoryIcon } from '../utils/categoryIcons';
import { identifyBrand, getLogoUrl } from '../utils/brandUtils';

interface CategoryDetailsDialogProps {
    open: boolean;
    onClose: () => void;
    category: string;
    expenses: Expense[];
    categoryIcons?: { [key: string]: string };
}

const CategoryDetailsDialog: React.FC<CategoryDetailsDialogProps> = ({
    open,
    onClose,
    category,
    expenses,
    categoryIcons = {}
}) => {
    const theme = useTheme();

    // Sort expenses by date descending
    const sortedExpenses = [...expenses].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    const totalAmount = sortedExpenses.reduce((sum, expense) => sum + expense.amount, 0);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: designTokens.borderRadius.lg,
                    background: designTokens.colors.background.paper,
                    border: designTokens.glassmorphism.border,
                    boxShadow: designTokens.shadows.xl
                }
            }}
        >
            <DialogTitle sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: `1px solid ${designTokens.colors.background.elevated}`,
                pb: 2
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {getCategoryIcon(category, categoryIcons)}
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: designTokens.typography.fontWeight.bold }}>
                            {category}
                        </Typography>
                        <Typography variant="caption" sx={{ color: designTokens.colors.text.secondary }}>
                            {expenses.length} <FormattedMessage id="expenses" defaultMessage="expenses" />
                        </Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClose} size="small">
                    <Close />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 0 }}>
                <TableContainer component={Box} sx={{ maxHeight: 500 }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ background: designTokens.colors.background.paper, fontWeight: 'bold' }}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <CalendarToday fontSize="small" sx={{ color: designTokens.colors.text.secondary }} />
                                        <FormattedMessage id="date" defaultMessage="Date" />
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ background: designTokens.colors.background.paper, fontWeight: 'bold' }}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Search fontSize="small" sx={{ color: designTokens.colors.text.secondary }} />
                                        <FormattedMessage id="description" defaultMessage="Description" />
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ background: designTokens.colors.background.paper, fontWeight: 'bold' }}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Person fontSize="small" sx={{ color: designTokens.colors.text.secondary }} />
                                        <FormattedMessage id="payer" defaultMessage="Payer" />
                                    </Box>
                                </TableCell>
                                <TableCell align="right" sx={{ background: designTokens.colors.background.paper, fontWeight: 'bold' }}>
                                    <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
                                        <Euro fontSize="small" sx={{ color: designTokens.colors.text.secondary }} />
                                        <FormattedMessage id="amount" defaultMessage="Amount" />
                                    </Box>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedExpenses.length > 0 ? (
                                sortedExpenses.map((expense) => {
                                    const brand = identifyBrand(expense.description);
                                    return (
                                        <TableRow key={expense.id} hover>
                                            <TableCell>
                                                <Typography variant="body2" color="text.secondary">
                                                    {new Date(expense.date).toLocaleDateString()}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                    {brand && (
                                                        <Box
                                                            component="img"
                                                            src={getLogoUrl(brand.domain)}
                                                            alt={brand.name}
                                                            sx={{
                                                                width: 20,
                                                                height: 20,
                                                                borderRadius: '4px',
                                                                flexShrink: 0
                                                            }}
                                                            onError={(e: any) => {
                                                                e.target.style.display = 'none';
                                                            }}
                                                        />
                                                    )}
                                                    <Typography variant="body2" sx={{ fontWeight: designTokens.typography.fontWeight.medium }}>
                                                        {expense.description}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={expense.payer}
                                                    size="small"
                                                    sx={{
                                                        height: 20,
                                                        fontSize: '0.75rem',
                                                        bgcolor: designTokens.colors.background.elevated
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography variant="body2" sx={{ fontWeight: 'bold', color: designTokens.colors.text.primary }}>
                                                    €{expense.amount.toFixed(2)}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            <FormattedMessage id="noExpensesFound" defaultMessage="No expenses found" />
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Box sx={{
                    p: 2,
                    borderTop: `1px solid ${designTokens.colors.background.elevated}`,
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    gap: 2,
                    bgcolor: designTokens.colors.background.paper
                }}>
                    <Typography variant="body1" color="text.secondary">
                        <FormattedMessage id="total" defaultMessage="Total" />:
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: designTokens.colors.primary.main }}>
                        €{totalAmount.toFixed(2)}
                    </Typography>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default CategoryDetailsDialog;
